import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { VariableSizeList } from 'react-window';

// The first virtual row is always the "header" (carousels + section heading,
// or the loading / empty state). Venue cards occupy rows 1..N. Keeping the
// header inside the same virtualized scroller preserves the single-scroll UX
// (carousels scroll away with the list) while only the venue cards near the
// viewport stay mounted in the DOM.
const HEADER_INDEX = 0;
const ROW_GAP = 14; // vertical gap between venue cards (matches legacy margin)
const ESTIMATED_HEADER_HEIGHT = 640;
const ESTIMATED_CARD_HEIGHT = 348;
// Extra scrollable space at the bottom so the last card clears the fixed
// mini-map + bottom nav that float over the list.
const FOOTER_PADDING = 188;

// Outer (scrolling) element keeps the legacy ids/attributes so
// useScrollNavCompact + jumpToTop keep targeting the right scroll surface.
// NOTE: We do NOT set paddingLeft/Right here. react-window absolutely
// positions each row at `left: 0` relative to its positioned container's
// PADDING-BOX (not content-box), so outer padding has no effect on row
// position — it just creates dead space on the right and clips
// negative-margin content on the left. Instead, each non-header row applies
// its own 16px horizontal padding (see VirtualRow below) so cards are inset,
// while the header row stays edge-to-edge so the carousels can bleed nicely.
const OuterElement = forwardRef(function OuterElement({ style, ...rest }, ref) {
  return (
    <div
      ref={ref}
      id="guest-map-scroll-container"
      data-testid="guest-map-scroll-container"
      data-jump-scroll-container="true"
      style={style}
      {...rest}
    />
  );
});

// Inner sizing element gets extra bottom padding so the final card can scroll
// clear of the floating map / bottom nav.
const InnerElement = forwardRef(function InnerElement({ style, ...rest }, ref) {
  return (
    <div
      ref={ref}
      style={{ ...style, height: `${parseFloat(style.height) + FOOTER_PADDING}px` }}
      {...rest}
    />
  );
});

function VirtualRow({ index, style, data }) {
  const { venues, header, renderItem, expandedVenueId, setRowHeight } = data;
  const innerRef = useRef(null);

  // Measure the natural height of the row content and report it (plus the
  // inter-card gap) so the list reserves the correct space. A ResizeObserver
  // keeps it accurate when a card expands/collapses or an image loads.
  useLayoutEffect(() => {
    const node = innerRef.current;
    if (!node) return undefined;
    const measure = () => {
      const h = node.offsetHeight;
      if (h > 0) {
        setRowHeight(index, index === HEADER_INDEX ? h : h + ROW_GAP);
      }
    };
    measure();
    const ro = new ResizeObserver(() => window.requestAnimationFrame(measure));
    ro.observe(node);
    return () => ro.disconnect();
  }, [index, setRowHeight]);

  if (index === HEADER_INDEX) {
    return (
      <div style={style}>
        <div ref={innerRef}>{header}</div>
      </div>
    );
  }

  const venue = venues[index - 1];
  if (!venue) return <div style={style} />;
  const isExpanded = expandedVenueId === venue.id;
  return (
    // Expanded rows float above neighbours so the card's social popup (which
    // intentionally overflows the card bounds) is not clipped by the next row.
    // 16px horizontal padding inset is applied here (not on the outer scroll
    // container) because react-window absolute-positions rows at left:0 of
    // the padding-box, ignoring outer padding.
    <div style={{ ...style, zIndex: isExpanded ? 5 : 1, paddingLeft: 16, paddingRight: 16, boxSizing: 'border-box' }}>
      <div ref={innerRef}>{renderItem(venue, index - 1, isExpanded)}</div>
    </div>
  );
}

/**
 * Virtualized venue list for the guest map. Renders the carousels + section
 * heading as the first virtual row and the venue cards as the remaining rows,
 * so only the cards near the viewport are mounted in the DOM. Scales to
 * thousands of venues with constant memory + smooth scrolling.
 */
export const VirtualizedVenueList = forwardRef(function VirtualizedVenueList(
  { header, venues, expandedVenueId, renderItem },
  apiRef,
) {
  const listRef = useRef(null);
  const wrapperRef = useRef(null);
  const rowHeights = useRef({});
  const [listHeight, setListHeight] = useState(() =>
    (typeof window !== 'undefined' ? Math.max(320, window.innerHeight - 360) : 480));

  // Measure the available scroll-area height for the virtual list viewport.
  useLayoutEffect(() => {
    const node = wrapperRef.current;
    if (!node) return undefined;
    const measure = () => {
      const h = node.clientHeight;
      if (h > 0) setListHeight(h);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(node);
    return () => ro.disconnect();
  }, []);

  const setRowHeight = useCallback((index, height) => {
    if (rowHeights.current[index] === height) return;
    rowHeights.current[index] = height;
    listRef.current?.resetAfterIndex(index);
  }, []);

  const getItemSize = useCallback((index) => {
    const cached = rowHeights.current[index];
    if (typeof cached === 'number') return cached;
    return index === HEADER_INDEX ? ESTIMATED_HEADER_HEIGHT : ESTIMATED_CARD_HEIGHT;
  }, []);

  // Recompute offsets whenever the venue set changes (filter / pan / sort).
  useEffect(() => {
    listRef.current?.resetAfterIndex(0);
  }, [venues]);

  useImperativeHandle(apiRef, () => ({
    scrollToVenue: (venueId) => {
      const i = venues.findIndex((v) => v.id === venueId);
      if (i >= 0) listRef.current?.scrollToItem(i + 1, 'start');
    },
    scrollToTop: () => listRef.current?.scrollTo(0),
  }), [venues]);

  const itemData = useMemo(() => ({
    venues,
    header,
    renderItem,
    expandedVenueId,
    setRowHeight,
  }), [venues, header, renderItem, expandedVenueId, setRowHeight]);

  return (
    <div ref={wrapperRef} style={{ flex: 1, minHeight: 0 }}>
      <VariableSizeList
        ref={listRef}
        height={listHeight}
        width="100%"
        itemCount={venues.length + 1}
        itemSize={getItemSize}
        estimatedItemSize={ESTIMATED_CARD_HEIGHT}
        overscanCount={3}
        itemData={itemData}
        outerElementType={OuterElement}
        innerElementType={InnerElement}
      >
        {VirtualRow}
      </VariableSizeList>
    </div>
  );
});

export default VirtualizedVenueList;
